import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAppTimeZoneOverride } from '@/hooks/use-user-preferences';
import { setUserPreferences } from '@/lib/stores/user-preference-store';
import { listTimeZones, systemDefaultTimeZone } from '@/lib/timezone';

// cmdk sentinel for the "follow the machine" row; maps to the "" override in the store.
const SYSTEM_VALUE = '__system__';

const pretty = (zone) => String(zone || '').replace(/_/g, ' ');

// Live "current time" line so the effect of a pick is visible in place, without leaving Settings.
function formatZoneNow(date, zone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'shortOffset',
    }).format(date);
  } catch {
    return '—';
  }
}

/**
 * Device-local "use one timezone everywhere" control. Writes the appTimeZone user preference;
 * src/lib/timezone.js turns that into luxon's default zone + the zone every native formatter uses,
 * so the whole app re-expresses times in it. "" (System default) restores the machine zone.
 */
export function TimezoneCard() {
  const override = useAppTimeZoneOverride(); // "" = follow the system zone
  const systemZone = systemDefaultTimeZone();
  const isOverridden = Boolean(override);
  const effectiveZone = override || systemZone;

  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Tick the preview clock. Local state, so only this card re-renders each second.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const zones = useMemo(() => listTimeZones(), []);

  const selectZone = (zone) => {
    const next = zone === SYSTEM_VALUE ? '' : zone;
    setUserPreferences({ appTimeZone: next });
    setOpen(false);
    toast.success(next ? `Global timezone set to ${pretty(next)}.` : "Using this device's timezone.");
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl">Timezone</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Saved on this device only. When set, the whole app — calendar, appointments, and message
          times — shows every time in this one zone instead of this computer's default.
        </p>

        <div className="flex items-center justify-between gap-4 py-1">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">App timezone</p>
            <p className="text-xs text-muted-foreground">
              {isOverridden
                ? 'Overriding the system default for the whole app.'
                : `Following this device (${pretty(systemZone)}).`}
            </p>
          </div>
          <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-label="App timezone"
                className="h-8 w-64 justify-between text-left text-sm font-normal"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="truncate">{isOverridden ? pretty(override) : 'System default'}</span>
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <Command>
                <CommandInput placeholder="Search timezones…" />
                <CommandList>
                  <CommandEmpty>No matching timezone.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value={`system default ${systemZone} ${pretty(systemZone)}`}
                      onSelect={() => selectZone(SYSTEM_VALUE)}
                    >
                      <Check className={cn('mr-2 h-4 w-4', !isOverridden ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">System default ({pretty(systemZone)})</span>
                    </CommandItem>
                    {zones.map((zone) => (
                      <CommandItem
                        key={zone}
                        value={`${zone} ${pretty(zone)}`}
                        onSelect={() => selectZone(zone)}
                      >
                        <Check
                          className={cn('mr-2 h-4 w-4', override === zone ? 'opacity-100' : 'opacity-0')}
                        />
                        <span className="truncate">{pretty(zone)}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Current time · {pretty(effectiveZone)}
          </p>
          <p className="font-mono text-sm font-semibold tabular-nums text-foreground">
            {formatZoneNow(now, effectiveZone)}
          </p>
        </div>

        <p className="text-[11px] text-muted-foreground">
          New views use the selected zone right away; reopen a page (or restart) to refresh anything
          already on screen.
        </p>
      </CardContent>
    </Card>
  );
}
